import { useState } from "react";
import { 
  BarChart3, Headphones, SlidersHorizontal, ChevronLeft, ChevronRight, 
  Users, CalendarClock, PhoneCall, Plus, CheckCircle2, ArrowRight
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { cn } from "../../lib/utils";
import { usePersistentState } from "../../hooks/use-persistent-state";
import { useAuth } from "../../context/AuthContext";
import { canAccessCrm } from "../../lib/userAccess";
import { crmApi } from "../../features/crm/api";
import { leadStatus } from "../../features/crm/types";

const categories = [
  { id: "sales", label: "Sales workspace", icon: BarChart3, href: "/crm" },
  { id: "calling", label: "Calling operations", icon: Headphones, href: "/crm/dialer" },
  { id: "management", label: "Management", icon: SlidersHorizontal, href: "/crm/assignment" },
];

export function sfaCategory(pathname: string): string {
  if (["/crm/dialer", "/crm/campaigns", "/crm/batches"].some((path) => pathname.startsWith(path))) return "calling";
  if (["/crm/assignment", "/crm/duplicates", "/crm/suppression", "/crm/agents", "/crm/sla", "/crm/users", "/crm/roles"].some((path) => pathname.startsWith(path))) return "management";
  return "sales";
}

export function SfaSidebar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const active = sfaCategory(pathname);
  const [collapsed, setCollapsed] = usePersistentState("sfa-sidebar:collapsed", false);
  
  const { user } = useAuth();
  const enabled = canAccessCrm(user);
  
  const leads = useQuery({ 
    queryKey: ["crm-leads"], 
    queryFn: crmApi.leads, 
    enabled, 
    refetchInterval: 30_000 
  });
  
  const followUps = useQuery({ 
    queryKey: ["crm-follow-ups"], 
    queryFn: crmApi.followUps, 
    enabled, 
    refetchInterval: 30_000 
  });

  const activeLeads = (leads.data ?? []).filter((lead) => !["Converted", "Not interested"].includes(leadStatus(lead)));
  const due = (followUps.data ?? []).filter((item) => !item.done && new Date(item.dueAt) <= new Date());
  const callingQueueCount = activeLeads.filter((lead) => !!lead.phone).length;

  return (
    <aside 
      className={cn(
        "hidden shrink-0 border-r border-line bg-elevated md:flex md:flex-col transition-all duration-200 select-none overflow-hidden h-full",
        collapsed ? "w-16" : "w-60"
      )}
      aria-label="SFA Navigation"
    >
      {/* ── Collapsed View ──────────────────────────── */}
      {collapsed ? (
        <div className="flex flex-col h-full items-center py-3">
          {/* Expand Button */}
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            aria-label="Expand SFA sidebar"
            title="Expand SFA sidebar"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-3 hover:bg-soft hover:text-ink transition-colors mb-3"
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          {/* Category Icons */}
          <div className="space-y-1 w-full px-2">
            {categories.map(({ id, label, icon: Icon, href }) => {
              const isActive = active === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => navigate(href)}
                  title={label}
                  className={cn(
                    "relative flex h-10 w-10 items-center justify-center rounded-lg mx-auto transition-colors",
                    isActive 
                      ? "bg-brand-soft text-brand font-semibold shadow-xs" 
                      : "text-ink-2 hover:bg-soft hover:text-ink"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {id === "sales" && due.length > 0 && (
                    <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-elevated" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="my-3 w-8 border-t border-line/60" />

          {/* Quick Icon Links */}
          <div className="space-y-1 w-full px-2">
            <Link
              to="/crm/leads"
              title="Leads"
              className="flex h-10 w-10 items-center justify-center rounded-lg mx-auto text-ink-3 hover:bg-soft hover:text-ink transition-colors"
            >
              <Users className="h-4 w-4" />
            </Link>
            <Link
              to="/crm/follow-ups"
              title="Follow-ups"
              className="relative flex h-10 w-10 items-center justify-center rounded-lg mx-auto text-ink-3 hover:bg-soft hover:text-ink transition-colors"
            >
              <CalendarClock className="h-4 w-4" />
              {due.length > 0 && (
                <span className="absolute top-1.5 right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[9px] font-bold text-white">
                  {due.length}
                </span>
              )}
            </Link>
            <Link
              to="/crm/dialer"
              title="Dialer"
              className="flex h-10 w-10 items-center justify-center rounded-lg mx-auto text-ink-3 hover:bg-soft hover:text-ink transition-colors"
            >
              <PhoneCall className="h-4 w-4" />
            </Link>
          </div>

          {/* Live Status Dot */}
          <div className="mt-auto pt-2" title="CRM Engine Live">
            <div className="flex h-6 w-6 items-center justify-center">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
            </div>
          </div>
        </div>
      ) : (
        /* ── Expanded View ──────────────────────────── */
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-line px-4 py-3 shrink-0">
            <div>
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-brand">SFA</p>
              <p className="text-xs font-semibold text-ink leading-tight">Sales automation</p>
            </div>
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              aria-label="Collapse SFA sidebar"
              title="Collapse SFA sidebar"
              className="flex h-7 w-7 items-center justify-center rounded-md text-ink-3 hover:bg-soft hover:text-ink transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 overflow-y-auto min-h-0 space-y-4 p-3">
            {/* Primary Category Nav */}
            <nav className="space-y-1" aria-label="SFA categories">
              {categories.map(({ id, label, icon: Icon, href }) => {
                const isActive = active === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => navigate(href)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-xs transition-colors",
                      isActive 
                        ? "bg-brand-soft font-semibold text-brand shadow-xs" 
                        : "text-ink-2 hover:bg-soft hover:text-ink"
                    )}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{label}</span>
                    </div>
                    {id === "sales" && activeLeads.length > 0 && (
                      <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-soft text-ink-2 font-normal">
                        {activeLeads.length}
                      </span>
                    )}
                    {id === "calling" && callingQueueCount > 0 && (
                      <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-brand/10 text-brand font-medium">
                        {callingQueueCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Quick Metrics Glance */}
            <div className="rounded-xl border border-line bg-soft/60 p-2.5 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Workspace Pulse</span>
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                </span>
              </div>
              <div className="grid grid-cols-3 gap-1 text-center">
                <Link 
                  to="/crm/leads" 
                  className="rounded-lg bg-elevated p-1.5 border border-line/60 hover:border-brand/40 transition-colors"
                >
                  <div className="font-mono text-sm font-bold text-ink">{activeLeads.length}</div>
                  <div className="text-[10px] text-ink-3 leading-tight truncate">Leads</div>
                </Link>
                <Link 
                  to="/crm/follow-ups" 
                  className={cn(
                    "rounded-lg bg-elevated p-1.5 border transition-colors",
                    due.length > 0 
                      ? "border-amber-400/50 bg-amber-500/5" 
                      : "border-line/60 hover:border-brand/40"
                  )}
                >
                  <div className={cn("font-mono text-sm font-bold", due.length > 0 ? "text-amber-600 dark:text-amber-400" : "text-ink")}>
                    {due.length}
                  </div>
                  <div className="text-[10px] text-ink-3 leading-tight truncate">Due</div>
                </Link>
                <Link 
                  to="/crm/dialer" 
                  className="rounded-lg bg-elevated p-1.5 border border-line/60 hover:border-brand/40 transition-colors"
                >
                  <div className="font-mono text-sm font-bold text-ink">{callingQueueCount}</div>
                  <div className="text-[10px] text-ink-3 leading-tight truncate">Queue</div>
                </Link>
              </div>
            </div>

            {/* Quick Actions Strip */}
            <div className="space-y-1.5">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3 px-1">Quick Actions</span>
              <div className="grid grid-cols-2 gap-1.5">
                <Link
                  to="/crm/leads"
                  className="flex items-center justify-center gap-1.5 rounded-lg border border-line bg-elevated px-2 py-1.5 text-[11px] font-medium text-ink hover:bg-soft hover:text-brand transition-colors"
                >
                  <Plus className="h-3.5 w-3.5 text-brand" />
                  <span>New lead</span>
                </Link>
                <Link
                  to="/crm/dialer"
                  className="flex items-center justify-center gap-1.5 rounded-lg border border-line bg-elevated px-2 py-1.5 text-[11px] font-medium text-ink hover:bg-soft hover:text-brand transition-colors"
                >
                  <PhoneCall className="h-3.5 w-3.5 text-brand" />
                  <span>Dialer</span>
                </Link>
              </div>
            </div>

            {/* Priority Follow-ups Mini-Queue */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between px-1">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Due Follow-ups</span>
                <Link 
                  to="/crm/follow-ups" 
                  className="text-[10px] text-brand hover:underline font-medium inline-flex items-center gap-0.5"
                >
                  <span>All</span>
                  <ArrowRight className="h-2.5 w-2.5" />
                </Link>
              </div>

              {due.length > 0 ? (
                <div className="space-y-1">
                  {due.slice(0, 3).map((item) => (
                    <Link
                      key={item.id}
                      to={`/crm/leads/${item.leadId}`}
                      className="block rounded-lg border border-line/70 bg-elevated p-2 hover:border-brand/50 hover:bg-soft transition-colors"
                    >
                      <div className="flex items-center justify-between gap-1.5">
                        <span className="truncate text-xs font-semibold text-ink">
                          {item.name || item.phone || "Lead"}
                        </span>
                        <span className="shrink-0 rounded bg-amber-500/10 px-1 py-0.2 text-[9px] font-semibold text-amber-600 dark:text-amber-400">
                          Due
                        </span>
                      </div>
                      {item.note && (
                        <p className="mt-0.5 truncate text-[11px] text-ink-3">
                          {item.note}
                        </p>
                      )}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg border border-line/60 bg-soft/40 px-2.5 py-2 text-ink-3">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                  <span className="text-[11px]">No overdue follow-ups</span>
                </div>
              )}
            </div>
          </div>

          {/* Footer Status */}
          <div className="border-t border-line px-3 py-2 shrink-0 flex items-center justify-between text-[11px] text-ink-3 bg-soft/30">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span>Real-time sync</span>
            </span>
            <span className="font-mono text-[10px]">v1.2</span>
          </div>
        </div>
      )}
    </aside>
  );
}
